/* eslint-disable */
import React, { useState, forwardRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import moment from "moment";
import "react-datepicker/dist/react-datepicker.css";
import "./styles.scss";
import { getMonth, getYear } from "date-fns";
import { months, years } from "../../../config/constants";
import { TextField } from "@mui/material";

// ✅ Enhanced CustomInput with better support for manual entry
const CustomInput = forwardRef(
  ({ className, onClick, value, onChange, ...props }, ref) => {
    const { t } = useTranslation();
    // Ensure value is never null
    const safeValue = value === null || value === undefined ? "" : value;

    return (
      <div className={`date-picker flex-container align-center ${className}`}>
        <input
          type="text"
          style={{
            width: "88%",
            paddingLeft: "2%",
            cursor: "pointer",
            height: "100%",
            borderRadius: "7px",
            border: "none",
            fontSize: "16px",
          }}
          placeholder={t("app:dateFormatHint")}
          value={safeValue}
          onChange={onChange}
          ref={ref}
          {...props}
        />
        <div className="date-icon" onClick={onClick} />
      </div>
    );
  }
);

const TextInput = forwardRef(
  (
    {
      label,
      compulsory,
      style,
      inputStyle,
      disabled,
      className,
      custom,
      value,
      onChange,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();
    // Ensure initial value is never null
    const initialValue = value ? moment(value).format("DD.MM.YYYY") : "";
    const [inputValue, setInputValue] = useState(initialValue);
    const [showCalendar, setShowCalendar] = useState(true);
    const [hasError, setHasError] = useState(false);

    // Auto-format and validate entry
    const formatAndValidateDate = (inputText) => {
      // Store original text to preserve leading zeros
      const originalText = inputText;
      let formattedText = inputText;

      // Remove any non-digits, dots, or slashes
      formattedText = formattedText.replace(/[^\d./-]/g, "");

      // Case for handling dots and slashes mixed (preserving leading zeros)
      if (/[\/.]/g.test(formattedText)) {
        // Replace all slashes with dots for consistent processing
        formattedText = formattedText.replace(/\//g, ".");

        // Split by dots and process each part
        const parts = formattedText.split(".");
        if (parts.length === 3) {
          // Check if each part is valid and preserve any leading zeros
          const day = parts[0].padStart(2, "0");
          const month = parts[1].padStart(2, "0");

          // Only use the first 4 digits for year
          const year = parts[2].length > 4 ? parts[2].slice(0, 4) : parts[2];

          if (
            parseInt(day) > 0 &&
            parseInt(day) <= 31 &&
            parseInt(month) > 0 &&
            parseInt(month) <= 12 &&
            year.length === 4
          ) {
            formattedText = `${day}.${month}.${year}`;

            // Create date object for full validation
            const dateObj = new Date(
              parseInt(year),
              parseInt(month) - 1,
              parseInt(day)
            );

            // Validate the date is real (e.g., not Feb 30)
            if (
              dateObj.getMonth() === parseInt(month) - 1 &&
              dateObj.getDate() === parseInt(day)
            ) {
              return { formatted: formattedText, isValid: true, dateObj };
            }

            // Date is formatted but not valid
            return { formatted: formattedText, isValid: false };
          }

          // Partial date with 3 parts
          return { formatted: formattedText, isValid: false };
        } else if (parts.length === 2) {
          // Format day and month, preserving leading zeros
          const day = parts[0].padStart(2, "0");
          const month = parts[1].padStart(2, "0");

          if (
            parseInt(day) > 0 &&
            parseInt(day) <= 31 &&
            parseInt(month) > 0 &&
            parseInt(month) <= 12
          ) {
            return { formatted: `${day}.${month}`, isValid: false };
          }
        }

        // Keep as is if it doesn't match patterns
        return { formatted: formattedText, isValid: false };
      }

      // Case for DD only
      if (/^\d{1,2}$/.test(formattedText)) {
        // Just leave it as is and wait for more input
        return { formatted: formattedText, isValid: false };
      }

      // Case for DDMM or DDMMYY or DDMMYYYY without separators
      if (/^\d{4,8}$/.test(formattedText)) {
        // For DDMM (4 digits)
        if (formattedText.length === 4) {
          const day = formattedText.substring(0, 2);
          const month = formattedText.substring(2, 4);

          // Basic validation
          if (
            parseInt(day) > 0 &&
            parseInt(day) <= 31 &&
            parseInt(month) > 0 &&
            parseInt(month) <= 12
          ) {
            return { formatted: `${day}.${month}`, isValid: false };
          }
        }

        // For DDMMYYYY (8 digits)
        if (formattedText.length === 8) {
          const day = formattedText.substring(0, 2);
          const month = formattedText.substring(2, 4);
          const year = formattedText.substring(4, 8);

          // Add dots
          formattedText = `${day}.${month}.${year}`;

          // Validate the complete date
          if (
            parseInt(day) > 0 &&
            parseInt(day) <= 31 &&
            parseInt(month) > 0 &&
            parseInt(month) <= 12 &&
            parseInt(year) >= 1900
          ) {
            const dateObj = new Date(
              parseInt(year),
              parseInt(month) - 1,
              parseInt(day)
            );

            // Final validation
            if (
              dateObj.getMonth() === parseInt(month) - 1 &&
              dateObj.getDate() === parseInt(day)
            ) {
              return { formatted: formattedText, isValid: true, dateObj };
            }
          }

          return { formatted: formattedText, isValid: false };
        }

        // For 5, 6, or 7 digits, preserve as is until it reaches 8
        return { formatted: formattedText, isValid: false };
      }

      // If none of the special cases match, return as is
      return { formatted: formattedText, isValid: false };
    };

    // Function to handle manually entered date
    const handleManualInput = (e) => {
      const inputText = e.target.value;

      // Only do format checking if the field isn't being cleared
      if (inputText.trim() !== "") {
        const { formatted, isValid, dateObj } =
          formatAndValidateDate(inputText);

        setInputValue(formatted);
        setHasError(!isValid && formatted.length >= 10);

        if (isValid && dateObj) {
          if (onChange) onChange(dateObj);
        }
      } else {
        // Handle empty input
        setInputValue("");
        setHasError(false);
        if (onChange) onChange(""); // Use empty string instead of null
      }
    };

    // Toggle between calendar and manual input
    const toggleInputMode = () => {
      setShowCalendar(!showCalendar);
      setHasError(false);
    };

    // Update input value when external value changes
    useEffect(() => {
      if (value) {
        setInputValue(moment(value).format("DD.MM.YYYY"));
      } else {
        setInputValue("");
      }
      setHasError(false);
    }, [value]);

    // Safely handle onChange
    const handleChange = (date) => {
      if (onChange) {
        if (date) {
          onChange(date);
        } else {
          onChange(""); // Use empty string instead of null
        }
      }
    };

    return (
      <div className="input-container" style={style}>
        {label && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <label
              style={{
                display: "flex",
                fontSize: "0.8rem",
                marginBottom: "5px",
              }}
            >
              {label}{" "}
              {compulsory && (
                <span style={{ paddingLeft: "5px", color: "black" }}>*</span>
              )}
            </label>
            <span
              onClick={toggleInputMode}
              style={{
                cursor: "pointer",
                fontSize: "0.7rem",
                color: "#0DC1CE",
                marginBottom: "5px",
                textDecoration: "underline",
              }}
            >
              {showCalendar ? t("app:manualEntry") : t("app:useCalendar")}
            </span>
          </div>
        )}

        {showCalendar ? (
          <DatePicker
            renderCustomHeader={
              custom
                ? ({
                    date,
                    changeYear,
                    changeMonth,
                    decreaseMonth,
                    increaseMonth,
                    prevMonthButtonDisabled,
                    nextMonthButtonDisabled,
                  }) => (
                    <div
                      style={{
                        margin: 10,
                        display: "flex",
                        justifyContent: "center",
                        gap: "5px",
                      }}
                    >
                      <button
                        onClick={decreaseMonth}
                        disabled={prevMonthButtonDisabled}
                      >
                        {"<"}
                      </button>
                      <select
                        value={getYear(date)}
                        onChange={({ target: { value } }) =>
                          changeYear(Number(value))
                        }
                      >
                        {years.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>

                      <select
                        value={months[getMonth(date)]}
                        onChange={({ target: { value } }) =>
                          changeMonth(months.indexOf(value))
                        }
                      >
                        {months.map((option) => (
                          <option key={option} value={option}>
                            {t(`app:months.${option}`)}
                          </option>
                        ))}
                      </select>

                      <button
                        onClick={increaseMonth}
                        disabled={nextMonthButtonDisabled}
                      >
                        {">"}
                      </button>
                    </div>
                  )
                : undefined
            }
            customInput={
              <CustomInput value={inputValue} onChange={handleManualInput} />
            }
            dateFormat="dd.MM.yyyy"
            selected={value}
            onChange={(date) => {
              if (date) {
                setInputValue(moment(date).format("DD.MM.YYYY"));
                handleChange(date);
              } else {
                setInputValue("");
                handleChange("");
              }
              setHasError(false);
            }}
            wrapperClassName={className}
            popperProps={{
              "data-calendar-container": "true",
              tabIndex: 0,
              style: { zIndex: 9999 },
            }}
            ref={ref}
            {...props}
          />
        ) : (
          // Field for manual date entry
          <input
            type="text"
            value={inputValue} // inputValue is already guaranteed to be a string
            onChange={handleManualInput}
            placeholder={t("app:dateFormatHint")}
            className={`manual-date-input ${className || ""} ${
              hasError ? "input-error" : ""
            }`}
            style={{
              width: "100%",
              padding: "8px 12px",
              borderRadius: "7px",
              border: hasError ? "1px solid #ff5252" : "1px solid #ccc",
              fontSize: "16px",
              boxSizing: "border-box",
            }}
          />
        )}

        {!showCalendar && (
          <div
            style={{
              fontSize: "0.7rem",
              color: hasError ? "#ff5252" : "#666",
              marginTop: "2px",
            }}
          >
            {hasError
              ? t("app:invalidDate")
              : t("app:enterDateFormat")}
          </div>
        )}
      </div>
    );
  }
);

export default TextInput;
